import { Checkbox, Text, VStack } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { kebabCase } from 'lodash';
import { Controller, UseFormReturn } from 'react-hook-form';

import { GasComposition } from '@/apis/makgeolli/factors/greenhouse-gases.dto';
import { IPCC } from '@/app/org-cfv/src/enums/CreateProjectEnum';
import { FormMode } from '@/app/org-cfv/src/enums/EmissionSourceEnum';
import SortableTable from '@/components/common/DataDisplay/table/SortableTable/SortableTable';
import { useNextTranslation } from '@/i18n/client';
import { useProjectStore } from '@/stores/org-cfv/useProjectStore';

import { RefrigerantSettingSchemaType } from './RefrigerantSettingModal';

type Props = {
  methods: UseFormReturn<RefrigerantSettingSchemaType, unknown, undefined>;
  mode: FormMode;
};
const RefrigerantBuiltInBlock = ({ methods, mode }: Props) => {
  const { t } = useNextTranslation();
  const { project } = useProjectStore();
  const arVersion = project?.projectVerificationEntity?.ipcc || IPCC.NONE;
  const curRefrigerant = methods.watch();

  const builtInColumns: ColumnDef<
    Pick<GasComposition, 'shortName' | 'ratio' | 'isMontrealRegulated' | 'isIncluded'>
  >[] = [
    {
      header: t('common.gasName'),
      id: kebabCase('shortName'),
      accessorKey: 'shortName',
      size: 282,
      cell: ({ getValue }) => getValue() || '-',
    },
    {
      header: `GWP ${t('common.percentage')}`,
      id: kebabCase('ratio'),
      accessorKey: 'ratio',
      meta: { align: 'right' },
      cell: ({ getValue }) => getValue() + '%' || '-',
    },
    {
      header: t('org-cfv-create-emission.regulatedGhg'),
      id: kebabCase('isMontrealRegulated'),
      accessorKey: 'isMontrealRegulated',
      meta: { align: 'center' },
      cell: ({ row: { original } }) => {
        return original.isMontrealRegulated ? t('common.no') : t('common.yes');
      },
    },
    {
      header: t('common.includeInEmissionCalc'),
      id: kebabCase('isIncluded'),
      accessorKey: 'isIncluded',
      meta: { align: 'center' },
      cell: ({ row: { index } }) => (
        <Controller
          name={`gasComposition.${index}.isIncluded`}
          control={methods.control}
          render={({ field: { value, ...field } }) => (
            <Checkbox size='lg' isChecked={value} isDisabled={mode === FormMode.VIEW} {...field} />
          )}
        />
      ),
    },
  ];

  return (
    <>
      <VStack gap={1} alignItems={'flex-start'} mb={4}>
        <Text fontSize='md' fontWeight='bold' lineHeight={1.2}>
          {t(`fugitive-emission.${curRefrigerant.refrigerant || ''}`)}
        </Text>
        <Text fontSize='sm' lineHeight={1.5}>
          {curRefrigerant.shortName}
        </Text>
        <Text fontSize='sm' lineHeight={1.5} color='blue.500'>{`GWP (${arVersion})：${
          curRefrigerant.gwp || '-'
        }`}</Text>
      </VStack>
      <SortableTable
        testId='export-report-table'
        data={curRefrigerant.gasComposition || []}
        columns={builtInColumns}
        isTableWidthAuto
        getRowCanExpand={() => false}
        sx={{
          table: {
            minWidth: '100%',
          },
        }}
      />
    </>
  );
};

export default RefrigerantBuiltInBlock;
